/**
 * SmsTab — Text BetOptimized (Admin → Communications → SMS).
 *
 * Only numbers in sms_allowlist/{+E164} can search by text (beta testers).
 * system_config/sms holds the kill switch and which data the texts use
 * (live, or the frozen snapshot with real prices). Recent texts come from
 * sms_jobs. All admin-only under the Firestore catch-all rule; the functions
 * live in functions/sms/ and the Twilio setup in docs/sms-setup.md.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, ScrollView, Platform } from 'react-native';
import { db } from '../firebaseConfig';
import { collection, deleteDoc, doc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { showToast } from '../utils/toast';
import { A, Pill, Panel, Btn, Segments, EmptyState, relTime } from './admin/adminUi';
import { AV, ADM_DISPLAY } from './floodlight/vars/admin';

const NUMBER = 'the SMS number (not set up yet)';
const STATUS_COLOR = { sent: A.green, queued: A.blue, send_failed: A.red, failed: A.red, not_configured: A.amber, skipped_opted_out: A.soft };

/** +E.164 or null; bare 10-digit and 1+10-digit numbers are US/Canada (same rule as functions/sms/core.js). */
export function normalizeE164(input) {
    const raw = String(input || '').trim();
    const digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+')) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return null;
}

const iso = (v) => (v?.toDate ? v.toDate().toISOString() : v instanceof Date ? v.toISOString() : v || null);
const inputStyle = { flexGrow: 1, flexBasis: 180, height: 38, paddingHorizontal: 10, borderRadius: 8, borderWidth: 1, borderColor: AV.line, color: AV.ink };

export default function SmsTab() {
    const [config, setConfig] = useState(null);
    const [allow, setAllow] = useState(null);
    const [optouts, setOptouts] = useState({});
    const [jobs, setJobs] = useState([]);
    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const unsubs = [
            onSnapshot(doc(db, 'system_config', 'sms'), (s) => setConfig(s.data() || {}), () => setConfig({})),
            onSnapshot(collection(db, 'sms_allowlist'), (s) => setAllow(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setAllow([])),
            onSnapshot(collection(db, 'sms_optout'), (s) => setOptouts(Object.fromEntries(s.docs.map((d) => [d.id, d.data()?.optedOut === true]))), () => {}),
            onSnapshot(query(collection(db, 'sms_jobs'), orderBy('createdAt', 'desc'), limit(25)), (s) => setJobs(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => {}),
        ];
        return () => unsubs.forEach((u) => u());
    }, []);

    const names = useMemo(() => Object.fromEntries((allow || []).map((a) => [a.id, a.name])), [allow]);

    const saveConfig = async (patch) => {
        try {
            await setDoc(doc(db, 'system_config', 'sms'), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
            showToast('SMS settings saved', 'success');
        } catch (e) { showToast(e.message, 'error'); }
    };

    const add = async () => {
        const e164 = normalizeE164(phone);
        if (!e164) { showToast('Enter a full phone number, e.g. 555 123 4567 or +44…', 'error'); return; }
        setBusy(true);
        try {
            await setDoc(doc(db, 'sms_allowlist', e164), { enabled: true, name: name.trim().slice(0, 80) || null, addedAt: serverTimestamp() }, { merge: true });
            setPhone(''); setName('');
            showToast(`${e164} can now text BetOptimized`, 'success');
        } catch (e) { showToast(e.message, 'error'); } finally { setBusy(false); }
    };

    const toggle = async (a) => {
        try { await setDoc(doc(db, 'sms_allowlist', a.id), { enabled: a.enabled === false, updatedAt: serverTimestamp() }, { merge: true }); } catch (e) { showToast(e.message, 'error'); }
    };

    const remove = async (a) => {
        if (Platform.OS === 'web' && !window.confirm(`Remove ${a.id}${a.name ? ` (${a.name})` : ''} from the SMS allow-list?`)) return;
        try { await deleteDoc(doc(db, 'sms_allowlist', a.id)); showToast('Removed', 'success'); } catch (e) { showToast(e.message, 'error'); }
    };

    if (!config || !allow) return <EmptyState icon="…" title="Loading SMS settings" />;
    const enabled = config.enabled !== false;
    const source = config.dataSource === 'frozen' ? 'frozen' : 'live';

    return (
        <ScrollView contentContainerStyle={{ padding: 16, gap: 16, paddingBottom: 60 }}>
            <View>
                <Text style={{ color: AV.ink, fontSize: 22, fontWeight: '800', ...ADM_DISPLAY }}>Text BetOptimized</Text>
                <Text style={{ color: AV.faint, fontSize: 12, marginTop: 2 }}>
                    Allow-listed beta testers text a question to {config.displayNumber || NUMBER} and get the top lines back. Texted searches don't use anyone's plan.
                </Text>
            </View>

            <Panel title="Settings" subtitle="Takes effect on the next text.">
                <View style={{ gap: 12 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Pill label={enabled ? 'On' : 'Paused'} color={enabled ? A.green : A.amber} />
                        <Btn small label={enabled ? 'Pause texting' : 'Turn texting on'} tone={enabled ? 'danger' : 'good'} onPress={() => saveConfig({ enabled: !enabled })} />
                    </View>
                    <View style={{ gap: 6 }}>
                        <Text style={{ color: AV.faint, fontSize: 12 }}>Data for texted answers</Text>
                        <Segments value={source} onChange={(v) => saveConfig({ dataSource: v })}
                            options={[{ value: 'live', label: 'Live' }, { value: 'frozen', label: 'Frozen snapshot' }]} />
                    </View>
                    <Text style={{ color: AV.faint, fontSize: 12 }}>
                        Limit per number: {config.hourlyLimit || 10}/hour, {config.dailyLimit || 40}/day (system_config/sms).
                    </Text>
                </View>
            </Panel>

            <Panel title="Allow-list" subtitle="Only these numbers get answers. HELP and STOP work for everyone.">
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                    <TextInput value={phone} onChangeText={setPhone} placeholder="Phone (555 123 4567)" placeholderTextColor={AV.faint} style={inputStyle} accessibilityLabel="Phone number" />
                    <TextInput value={name} onChangeText={setName} placeholder="Name (optional)" placeholderTextColor={AV.faint} style={inputStyle} accessibilityLabel="Name" />
                    <Btn label={busy ? 'Adding…' : 'Add number'} tone="primary" disabled={busy || !phone.trim()} onPress={add} />
                </View>
                {allow.length === 0 && <Text style={{ color: AV.faint }}>No numbers yet.</Text>}
                {allow.map((a) => (
                    <View key={a.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingVertical: 8, borderTopWidth: 1, borderColor: AV.line }}>
                        <Text style={{ color: AV.ink, fontWeight: '700', minWidth: 130 }}>{a.id}</Text>
                        <Text style={{ color: AV.soft, flexGrow: 1 }}>{a.name || '—'}</Text>
                        {optouts[a.id] && <Pill label="Opted out (STOP)" color={A.amber} />}
                        <Pill label={a.enabled === false ? 'Disabled' : 'Active'} color={a.enabled === false ? A.soft : A.green} />
                        <Text style={{ color: AV.faint, fontSize: 12 }}>{a.messageCount || 0} texts{a.lastMessageAt ? ` · last ${relTime(iso(a.lastMessageAt))}` : ''}</Text>
                        <Btn small label={a.enabled === false ? 'Enable' : 'Disable'} onPress={() => toggle(a)} />
                        <Btn small label="Remove" tone="danger" onPress={() => remove(a)} />
                    </View>
                ))}
            </Panel>

            <Panel title="Recent texts" subtitle="Last 25 questions and what was sent back.">
                {jobs.length === 0 && <Text style={{ color: AV.faint }}>No texts yet.</Text>}
                {jobs.map((j) => (
                    <View key={j.id} style={{ paddingVertical: 8, borderTopWidth: 1, borderColor: AV.line, gap: 4 }}>
                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Pill label={j.status || '?'} color={STATUS_COLOR[j.status] || A.soft} />
                            <Text style={{ color: AV.ink, fontWeight: '700' }}>"{j.prompt}"</Text>
                            <Text style={{ color: AV.faint, fontSize: 12 }}>
                                {names[j.from] || j.from} · {relTime(iso(j.createdAt))}{j.searchMs ? ` · ${(j.searchMs / 1000).toFixed(1)}s` : ''}{j.segments ? ` · ${j.segments} seg` : ''}{j.dataSource === 'frozen' ? ' · frozen' : ''}
                            </Text>
                        </View>
                        {!!j.reply && <Text style={{ color: AV.soft, fontSize: 12, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, whiteSpace: 'pre-wrap' }}>{j.reply}</Text>}
                        {!!(j.sendError || j.error) && <Text style={{ color: A.red, fontSize: 12 }}>{j.sendError || j.error}</Text>}
                    </View>
                ))}
            </Panel>
        </ScrollView>
    );
}
